import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
} from '@angular/core';
import { takeUntil, debounceTime, switchMap, tap } from 'rxjs/operators';
import {
  ruleSelectConfig,
  filterConfig,
  weightUnits,
  conditionsConfig,
  carrierFilterConfig,
  selectedFilterOptions,
  RuleNameToDisplayNameMap,
  ConditionFilterOptions,
  timePeriodOptions,
  grossMarginUnits,
  productTagsQuantityOperators,
  cartValueDiscountBasisOptions,
  defaultCartValueDiscountBasis,
} from '../../../configs';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, AbstractControl, Validators } from '@angular/forms';
import { FormService } from '../../../services';
import { initLocationsOptions, getCurrencyAndPercentOptions, getShopCountryCode } from '../../../utils';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { ShopService } from '@app/services/shops/shop.service';
import { CheckoutService } from '@pages/checkout-app/checkout.service';
import { Carrier } from '@app/services/carriers/carrier.model';
import { ECOMMERCES, Shop } from '@app/services/shops/shop.model';
import { DEFAULT_WEIGHT_UNIT } from '@pdqdev/pdq-shared/dist/entities';
import { CartValueDiscountBasis } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { currencyStrToSign } from 'src/utils';
import { CountryCode } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { Store } from '@ngrx/store';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';

export enum AutocompleteBy {
  VENDOR = 'vendor',
  COLLECTION = 'collection',
  WEIGHT = 'weight',
  PRODUCT_TYPE = 'productType',
  TAGS = 'tags',
  ORDER_TAG = 'order_tag.name',
  PRODUCT_TAG = 'product_tag.name',
  BARCODE = 'barcode',
  SKU = 'sku',
  SHOPIFY_TAGS = 'shopify_tags',
}

const NUMERIC_VALUES = ['weight', 'cartValue', 'coupons', 'customerSpend', 'grossMargin', 'sellingPlan'];

@Component({
  selector: 'app-condition',
  templateUrl: './condition.component.html',
  styleUrls: ['./condition.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ConditionComponent implements OnInit, OnDestroy {
  @Input() formGroup: UntypedFormGroup;
  @Input() accordionActiveSection: string[];
  @Input() loadedCarrier: Carrier;
  @Input() hideInstructionText: boolean = false;
  @Input() hideAddGroupButton: boolean = false;
  @Input() dropdownMaxHeight: string;
  @Input() useDropdownOverlay: boolean = false;
  @Output() onOpenExpansionEmit = new EventEmitter<string>();
  @Output() onCloseExpansionEmit = new EventEmitter<void>();
  @Output() onExpandedNeededEmit = new EventEmitter<string>();
  RulesDisplayNameMap: Map<string, string> = RuleNameToDisplayNameMap;
  autocompleteList$: Observable<any>;
  showExpansionGuide: string;
  ruleSelectConfig = [...ruleSelectConfig];
  conditionsConfig = [...conditionsConfig];
  carrierFilterConfig = carrierFilterConfig;
  selectedFilterOptions = { ...selectedFilterOptions };
  weightUnits = [...weightUnits];
  typeInput: string;
  weightUnit: string;
  shopSettings: { currency: string; weightUnits; countryCode: CountryCode };
  conditionSelects: string;
  autocompleteBy: string;

  showConditionsErrorMsg: string;
  collection: any = [];
  shop: Shop;
  shopPlatform: ECOMMERCES;
  currencyOrPercentOptions: ConditionFilterOptions[] = [];
  shippingMethodCurrency$;
  controlsSubscriptions$ = [];
  filterOptions: DropdownOption[] = [];
  weightUnitsOptions: DropdownOption[] = [];
  grossMarginUnitOptions: DropdownOption[] = [];
  productTagsQuantityOptions: DropdownOption[] = [];
  cartValueDiscountBasisOptions: DropdownOption[] = cartValueDiscountBasisOptions;
  defaultCartValueBasis: CartValueDiscountBasis | null = null;
  defaultCartValueBasisLabel: string;
  autoCompleteCollections: {
    [key: string]: {
      isLoading: boolean;
      list: any[];
      initialList: any[];
    };
  } = {};
  showFormErrors: boolean;
  private destroy$ = new Subject();
  customerOrderCountTimePeriodOptions: DropdownOption[] = timePeriodOptions;

  constructor(
    private formService: FormService,
    private shopService: ShopService,
    private formBuilder: UntypedFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private cs: CheckoutService,
    private store: Store<fromCheckoutReducer.State>,
  ) {}
  ngOnInit() {
    this.shippingMethodCurrency$ = this.cs.shippingMethodCurrency$;
    filterConfig.map((filter) => {
      this.filterOptions.push({
        label: filter.name,
        value: filter.value,
        value2: filter.operatorId,
        type: filter.supportedPlatforms,
      });
    });
    this.initShopService();
    this.conditionsField.value.forEach((conditions, index) => {
      this.controlsSubscriptions$.push([]);
      conditions.forEach((condition, subIndex) => {
        this.controlsSubscriptions$[index].push(null);
        this.loadAutocompleteByType(
          condition.name,
          index,
          subIndex,
          (this.conditionsField.controls[index] as UntypedFormGroup).controls[subIndex] as UntypedFormGroup,
        );
      });
    });
    if (this.conditionsField.controls.length === 0) {
      this.addConditions(true);
    }

    this.weightUnitsOptions = weightUnits.map((unit) => {
      return { value: unit.value, label: unit.name };
    });

    this.grossMarginUnitOptions = grossMarginUnits.map(({ value, name }) =>
      name === 'symbol'
        ? { value: this.shopSettings.currency, label: currencyStrToSign(this.shopSettings.currency) }
        : { value, label: name },
    );

    this.productTagsQuantityOptions = productTagsQuantityOperators.map(({ value, name }) => {
      return { value, label: name };
    });
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  initShopService() {
    this.shopService.shop$.pipe(takeUntil(this.destroy$)).subscribe((shop) => {
      this.shop = shop;
      this.shopPlatform = shop.platform;
      initLocationsOptions(this.shop);

      this.shopSettings = {
        currency: shop.metadata?.currency,
        weightUnits: shop.metadata?.weightUnits,
        countryCode: getShopCountryCode(shop) as CountryCode,
      };
      this.initAdditionalConditions();
    });

    this.shippingMethodCurrency$.pipe(takeUntil(this.destroy$)).subscribe((curr) => {
      this.currencyOrPercentOptions = getCurrencyAndPercentOptions(currencyStrToSign(curr));
    });

    // old rules keep a null basis (shop setting), shown as placeholder so saving doesn't rewrite them
    this.store
      .select(fromCheckoutReducer.selectSettings)
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        // null until settings load, a guessed basis would get saved on the row
        this.defaultCartValueBasis = settings
          ? defaultCartValueDiscountBasis(settings.subTotalIncludesDiscounts)
          : null;
        this.defaultCartValueBasisLabel = this.cartValueDiscountBasisOptions.find(
          ({ value }) => value === this.defaultCartValueBasis,
        )?.label;
        this.changeDetectorRef.markForCheck();
      });
  }

  setAdditionalConditionsConfig(carrierTypeName) {
    if (!carrierTypeName) {
      return;
    }
    const additionalConditionsConfig = carrierFilterConfig[carrierTypeName];

    if (additionalConditionsConfig) {
      this.filterOptions = this.filterOptions.concat(
        additionalConditionsConfig.map((filter) => ({
          label: filter.name,
          value: filter.value,
          value2: filter.operatorId,
          type: filter.supportedPlatforms,
        })),
      );
    }
  }

  initAdditionalConditions() {
    this.filterOptions = this.filterOptions?.filter((filter) => filter.type.includes(this.shopPlatform));
    this.setAdditionalConditionsConfig(this.loadedCarrier?.type?.name);
    this.cs.onSetSelectedCarrier$.pipe(takeUntil(this.destroy$)).subscribe((carrier) => {
      this.filterOptions = [
        ...filterConfig.map((filter) => ({
          label: filter.name,
          value: filter.value,
          value2: filter.operatorId,
          type: filter.supportedPlatforms,
        })),
      ];
      this.setAdditionalConditionsConfig(carrier?.type?.name);
      this.filterOptions = this.filterOptions?.filter((filter) => filter.type.includes(this.shopPlatform));
    });
  }

  filterConditionBySelectedFilter(name: string) {
    const filterItem = filterConfig.find((filter) => filter.value === name);
    return this.conditionsConfig
      .filter((item) => item.parentId === filterItem?.operatorId)
      .map((item) => ({
        label: item.name,
        value: item.value,
        value2: item.operatorId,
        type: item.supportedPlatforms,
      }));
  } // end conditions section

  loadAutocompleteByType(
    autocompleteBy: string,
    conditionsIndex: number,
    conditionIndex: number,
    conditionControl: UntypedFormGroup,
  ): void {
    if (!this.autoCompleteCollections[autocompleteBy]) {
      this.autoCompleteCollections[autocompleteBy] = { isLoading: true, list: [], initialList: [] };
    }
    this.formService.conditionsAutocomplete('', autocompleteBy).subscribe((res) => {
      this.autoCompleteCollections[autocompleteBy].list = res;
      this.autoCompleteCollections[autocompleteBy].initialList = res;
      this.autoCompleteCollections[autocompleteBy].isLoading = false;
      this.changeDetectorRef.detectChanges();
    });
    this.initCollectionAutocomplete(conditionsIndex, conditionIndex, conditionControl, autocompleteBy);
  }

  initCollectionAutocomplete(
    conditionsIndex: number,
    conditionIndex: number,
    conditionControl: UntypedFormGroup,
    autocompleteBy: string,
  ): void {
    this.controlsSubscriptions$[conditionsIndex][conditionIndex] = conditionControl
      .get('value1')
      .valueChanges.pipe(
        debounceTime(500),
        switchMap((value: string) => {
          if (!value || value.length < 2) {
            if (this.autoCompleteCollections[autocompleteBy]) {
              this.autoCompleteCollections[autocompleteBy].list =
                this.autoCompleteCollections[autocompleteBy].initialList;
              this.autoCompleteCollections[autocompleteBy].isLoading = false;
              this.changeDetectorRef.detectChanges();
            }
            return of([]);
          }
          if (this.autoCompleteCollections[autocompleteBy]) {
            this.autoCompleteCollections[autocompleteBy].isLoading = true;
            this.changeDetectorRef.detectChanges();
          }
          return this.formService.conditionsAutocomplete(value, autocompleteBy).pipe(
            tap((res) => {
              this.autoCompleteCollections[autocompleteBy].list = res;
              this.autoCompleteCollections[autocompleteBy].isLoading = false;
              this.changeDetectorRef.detectChanges();
            }),
          );
        }),
      )
      .subscribe();
  }

  get conditionsField(): UntypedFormArray {
    return this.formGroup.get('conditions') as UntypedFormArray;
  }

  getConditionFilterOptions(name): ConditionFilterOptions[] {
    const options = selectedFilterOptions[name];
    return options?.filter((option) => (option.display ? option.display(this.shopSettings.countryCode) : !!option));
  }

  onConditionOperatorSelect(event) {
    // reset fields
    this.typeInput = undefined;
    this.formGroup.controls['conditionDropDown'].reset();
  }

  isNotNumericFilterSelected(str: string) {
    return !str || !NUMERIC_VALUES.includes(str);
  }

  onOpenExpansion(section: string): boolean {
    this.onOpenExpansionEmit.emit(section);
    return true;
  }

  onCloseExpansion(): void {
    this.onCloseExpansionEmit.emit();
  }

  isExpandedNeeded(section: string): boolean {
    return this.accordionActiveSection.some((value) => value == section);
  }

  addConditions(firstInitial?: boolean) {
    this.addCondition(this.controlsSubscriptions$.length, firstInitial);
  }

  addCondition(conditionsIndex: number, firstInitial?: boolean) {
    if (!firstInitial) {
      const count = this.conditionsField.controls.reduce(
        (total, formArray: UntypedFormArray) => total + formArray.controls.length,
        0,
      );
      if (count >= 25) {
        this.showConditionsErrorMsg = `The maximum shipping rules you can add is 25`;
        return;
      }

      if (this.conditionsField.controls.some((group: UntypedFormArray) => group.controls.some((c) => !c.valid))) {
        return;
      }
    }
    if (!this.conditionsField.controls[conditionsIndex]) {
      this.controlsSubscriptions$.push([]);
      this.conditionsField.push(this.formBuilder.array([]));
    }
    this.controlsSubscriptions$[conditionsIndex].push(null);
    // raw controls.push leaves the row parentless and its group's validity stale
    (this.conditionsField.controls[conditionsIndex] as UntypedFormArray).push(
      this.formBuilder.group({
        name: [null, Validators.required],
        displayName: [null, Validators.required],
        condition: [null, Validators.required],
        values: null,
        weightUnit: null,
        value1: [null, Validators.required],
        value2: null,
        optionalValue: null,
        stringParam: null,
      }),
    );
  }

  // onpush: changes made from outside (review revert) must re-render the list
  restoreCondition(conditionsIndex: number, control: UntypedFormGroup): void {
    if (!this.conditionsField.controls[conditionsIndex]) {
      this.controlsSubscriptions$.push([]);
      this.conditionsField.push(this.formBuilder.array([]));
      conditionsIndex = this.conditionsField.length - 1;
    }
    const conditions = this.conditionsField.controls[conditionsIndex] as UntypedFormArray;
    conditions.push(control);
    this.controlsSubscriptions$[conditionsIndex].push(null);
    this.loadAutocompleteByType(control.value.name, conditionsIndex, conditions.length - 1, control);
    this.changeDetectorRef.markForCheck();
  }

  deleteCondition(conditionsIndex: number, conditionIndex: number) {
    const conditionsArray = this.conditionsField.controls[conditionsIndex] as UntypedFormArray;
    conditionsArray.removeAt(conditionIndex);
    this.controlsSubscriptions$[conditionsIndex][conditionIndex]?.unsubscribe();
    this.controlsSubscriptions$[conditionsIndex].splice(conditionIndex, 1);
    if (!conditionsArray.controls.length) {
      this.conditionsField.removeAt(conditionsIndex);
      this.controlsSubscriptions$.splice(conditionsIndex, 1);
    }
    this.markControlAsDirty(this.conditionsField);
    this.changeDetectorRef.markForCheck();
  }

  markControlAsDirty(control: AbstractControl) {
    control.markAsDirty();
    control.updateValueAndValidity();
  }

  getConditionsControls(conditionsIndex: number) {
    return (this.conditionsField.controls[conditionsIndex] as UntypedFormArray).controls;
  }

  entityChanged(
    data,
    conditionControl: UntypedFormGroup,
    conditionsIndex: number,
    conditionIndex: number,
    conditionsGroup: UntypedFormGroup,
  ) {
    conditionControl.controls.name.setValue(data.value);
    conditionControl.controls.displayName.setValue(data.label);
    this.markControlAsDirty(conditionControl.controls.name);
    conditionControl.controls.condition.setValue(null);

    this.markControlAsDirty(conditionsGroup);
    if (this.controlsSubscriptions$[conditionsIndex][conditionIndex] instanceof Subscription) {
      this.controlsSubscriptions$[conditionsIndex][conditionIndex].unsubscribe();
    }
    conditionControl.get('value1').setValue(null);
    // optionalValue means something else per type, a leftover becomes NaN days in checkout
    conditionControl.controls.optionalValue?.setValue(null);
    if (data.value === 'weight') {
      conditionControl.controls.weightUnit.setValue(this.shopSettings?.weightUnits ?? DEFAULT_WEIGHT_UNIT);
    } else {
      conditionControl.controls.weightUnit.setValue(null);
    }
    if (data?.value === 'customerOrderCount') {
      conditionControl.controls.value2.setValue(
        conditionControl.controls.value2.value ?? this.customerOrderCountTimePeriodOptions[0].value,
      );
    }

    if (data.value === 'grossMargin') {
      conditionControl.controls.optionalValue?.setValue(this.shopSettings.currency);
    }
    if (data.value === 'cartValue') {
      conditionControl.controls.optionalValue?.setValue(this.defaultCartValueBasis);
    }
    if (data?.value === 'shopify_tags') {
      conditionControl.controls.value2.setValue(
        conditionControl.controls.value2.value ?? this.productTagsQuantityOptions[0].value,
      );
    }
  }

  getConditionLabelByValue(value: string) {
    return this.conditionsConfig.find((filter) => filter.value === value)?.name;
  }

  conditionChanged(
    data,
    conditionControl: UntypedFormGroup,
    conditionsIndex: number,
    conditionIndex: number,
    conditionsGroup: UntypedFormGroup,
  ) {
    conditionControl.controls.condition.setValue(data.value);
    this.markControlAsDirty(conditionControl.controls.condition);
    this.markControlAsDirty(conditionsGroup);
    if (this.controlsSubscriptions$[conditionsIndex][conditionIndex] instanceof Subscription) {
      this.controlsSubscriptions$[conditionsIndex][conditionIndex].unsubscribe();
    }
    conditionControl.get('value1').setValue(null);
    this.loadAutocompleteByType(
      conditionControl.controls.name.value,
      conditionsIndex,
      conditionIndex,
      conditionControl,
    );
  }

  getConditionFilterOptionLabel(name: string, selectedValue: string) {
    const options = this.getConditionFilterOptions(name);
    return options.find((op) => op.value == selectedValue)?.label;
  }

  // getConditionError(conditionControl: FormGroup, conditionIndex: number, displayRequiredSign = true) {
  //   return '';
  // }
  hasAnyInvalidCondition(): boolean {
    return this.conditionsField.controls.some((conditionsGroup: UntypedFormArray) =>
      conditionsGroup.controls.some((conditionGroup: UntypedFormGroup) => conditionGroup.invalid),
    );
  }
}
